import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, MapPin } from 'lucide-react';

let scriptPromise = null;
let cachedApiKey = null;

async function ensureGoogleMapsLoaded() {
    if (typeof window === 'undefined') return null;
    if (window.google?.maps?.places) return window.google;
    if (scriptPromise) return scriptPromise;

    // Fetch the browser API key from our backend
    if (!cachedApiKey) {
        try {
            const { data } = await api.get('/settings/maps-key');
            cachedApiKey = data.api_key || '';
        } catch { cachedApiKey = ''; }
    }
    if (!cachedApiKey) return null;

    scriptPromise = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = `https://maps.googleapis.com/maps/api/js?key=${cachedApiKey}&libraries=places&loading=async&v=weekly`;
        s.async = true;
        s.defer = true;
        s.onload = () => resolve(window.google);
        s.onerror = () => { scriptPromise = null; reject(new Error('Google Maps script failed')); };
        document.head.appendChild(s);
    });
    return scriptPromise;
}

/**
 * GoogleMapsAddressPicker
 * - Autocomplete Places input
 * - Interactive map with draggable pin
 * - Falls back to plain input if no API key configured.
 * Props:
 *   value: { address, lat, lng, city, state, country, postal_code, place_id }
 *   onChange(next)
 */
export function GoogleMapsAddressPicker({ value, onChange, label = 'Dirección del anuncio', testId = 'gmaps-picker' }) {
    const inputRef = useRef(null);
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markerInstance = useRef(null);
    const [ready, setReady] = useState(false);
    const [status, setStatus] = useState('loading');
    const [manualAddress, setManualAddress] = useState(value?.address || '');

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const g = await ensureGoogleMapsLoaded();
                if (cancel) return;
                if (!g) { setStatus('nokey'); return; }
                setReady(true);
                setStatus('ready');
                // Init autocomplete
                const ac = new g.maps.places.Autocomplete(inputRef.current, {
                    fields: ['formatted_address', 'geometry', 'address_components', 'place_id'],
                });
                ac.addListener('place_changed', () => {
                    const p = ac.getPlace();
                    if (!p?.geometry) return;
                    const lat = p.geometry.location.lat();
                    const lng = p.geometry.location.lng();
                    const parsed = extractComponents(p.address_components || []);
                    const next = {
                        address: p.formatted_address || '',
                        lat, lng,
                        place_id: p.place_id || '',
                        ...parsed,
                    };
                    onChange && onChange(next);
                    if (mapInstance.current && markerInstance.current) {
                        mapInstance.current.setCenter({ lat, lng });
                        mapInstance.current.setZoom(15);
                        markerInstance.current.setPosition({ lat, lng });
                    }
                });

                // Init map
                const startLat = value?.lat || 19.4326;
                const startLng = value?.lng || -99.1332;
                mapInstance.current = new g.maps.Map(mapRef.current, {
                    center: { lat: startLat, lng: startLng },
                    zoom: value?.lat ? 15 : 5,
                    mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
                });
                markerInstance.current = new g.maps.Marker({
                    position: { lat: startLat, lng: startLng },
                    map: mapInstance.current,
                    draggable: true,
                });
                markerInstance.current.addListener('dragend', async () => {
                    const pos = markerInstance.current.getPosition();
                    const lat = pos.lat(); const lng = pos.lng();
                    // reverse geocode
                    try {
                        const geo = new g.maps.Geocoder();
                        geo.geocode({ location: { lat, lng } }, (results, s) => {
                            if (s === 'OK' && results?.[0]) {
                                const p = results[0];
                                const parsed = extractComponents(p.address_components || []);
                                onChange && onChange({
                                    address: p.formatted_address || '',
                                    lat, lng,
                                    place_id: p.place_id || '',
                                    ...parsed,
                                });
                                inputRef.current.value = p.formatted_address || '';
                            } else {
                                onChange && onChange({ ...(value || {}), lat, lng });
                            }
                        });
                    } catch { onChange && onChange({ ...(value || {}), lat, lng }); }
                });
            } catch (e) {
                setStatus('error');
            }
        })();
        return () => { cancel = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (status === 'nokey') {
        // fallback: plain text input
        return (
            <div className="space-y-1" data-testid={`${testId}-fallback`}>
                <Label className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{label}</Label>
                <Input
                    value={manualAddress}
                    onChange={(e) => { setManualAddress(e.target.value); onChange && onChange({ ...(value || {}), address: e.target.value }); }}
                    placeholder="Escribe la dirección…"
                    data-testid={`${testId}-input-manual`}
                />
                <p className="text-[11px] text-amber-700">Google Maps no está configurado. Configura la API Key en Superadmin → Ajustes → Integraciones para autocompletar y mapa interactivo.</p>
            </div>
        );
    }

    return (
        <div className="space-y-2" data-testid={testId}>
            <Label className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{label}</Label>
            <Input
                ref={inputRef}
                defaultValue={value?.address || ''}
                placeholder="Empieza a escribir una dirección…"
                data-testid={`${testId}-input`}
            />
            <div className="relative">
                <div ref={mapRef} className="h-56 sm:h-64 rounded-xl border overflow-hidden bg-neutral-100" data-testid={`${testId}-map`} />
                {!ready && (
                    <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin mr-1" />Cargando mapa…
                    </div>
                )}
            </div>
            {value?.lat && (
                <div className="text-[11px] text-muted-foreground">
                    Lat/Lng: {value.lat.toFixed(5)}, {value.lng.toFixed(5)}
                    {value.city && ` · ${value.city}${value.state ? `, ${value.state}` : ''}`}
                </div>
            )}
        </div>
    );
}

function extractComponents(components) {
    const out = { city: '', state: '', country: '', postal_code: '' };
    components.forEach(c => {
        const types = c.types || [];
        if (types.includes('locality')) out.city = c.long_name;
        else if (types.includes('administrative_area_level_1')) out.state = c.long_name;
        else if (types.includes('country')) out.country = c.long_name;
        else if (types.includes('postal_code')) out.postal_code = c.long_name;
    });
    return out;
}

export default GoogleMapsAddressPicker;
